/**
 * Gift With Purchase (Back To School Minis) — visual + line-item marker only.
 * Does NOT add the gift product to the cart.
 * Stamps subscription line properties + cart attributes so Flow can add the gift later.
 *
 * Rule: if the cart has at least one subscription line (PDP or cart upsell),
 * stamp GWP once and set cart attributes. Clear when no subscriptions remain.
 *
 * Line item properties: _gwp, _gwp_source
 * Cart attributes: free_gift_product_id, free_gift_variant_id
 *
 * Never offered on wholesale or landing templates:
 * page.pdp-ads, page.5-reason, page.10-reasons-2 (and related ads pages).
 */
(function () {
  const GWP_HANDLE = 'variety-travel-2-pack-of-1-oz';
  const GWP_PROPERTY = 'back_to_school_minis';
  const GWP_SOURCE_PDP = 'pdp';
  const GWP_SOURCE_CART = 'cart';
  const ATTR_PRODUCT_ID = 'free_gift_product_id';
  const ATTR_VARIANT_ID = 'free_gift_variant_id';

  window.RevitinGWP = window.RevitinGWP || {
    handle: GWP_HANDLE,
    productId: null,
    variantId: null,
    enabled: true,
  };

  const BLOCKED_TEMPLATE_HINTS = [
    'pdp-ads',
    '5-reason',
    '5-reasons',
    '10-reasons-2',
    '10-reasons-1',
    'page.ads',
    '/pages/ads',
  ];

  const isGwpEnabled = () => {
    if (!window.RevitinGWP || window.RevitinGWP.enabled === false) return false;
    if (window.RevitinGWP.isWholesale === true) return false;
    const flag =
      document.documentElement?.getAttribute('data-gwp-enabled') ||
      document.body?.getAttribute('data-gwp-enabled');
    if (flag === 'false') return false;
    const path = (window.location.pathname || '').toLowerCase();
    if (BLOCKED_TEMPLATE_HINTS.some((hint) => path.includes(hint))) return false;
    return true;
  };

  const fetchJSON = (url, options = {}) =>
    fetch(url, {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...options,
    }).then((res) => res.json());

  const getCart = () => fetchJSON('/cart.js');

  const hasGwpMarker = (item) =>
    item.properties && item.properties._gwp === GWP_PROPERTY;

  const isGiftProduct = (item) =>
    item.handle === GWP_HANDLE || item.product_handle === GWP_HANDLE;

  const isSubscriptionItem = (item) =>
    item.selling_plan_allocation != null && !isGiftProduct(item);

  const cartHasGwpMarker = (cart) => (cart.items || []).some(hasGwpMarker);

  const cartHasGiftAttributes = (cart) => {
    const attrs = cart && cart.attributes ? cart.attributes : {};
    return !!(attrs[ATTR_PRODUCT_ID] || attrs[ATTR_VARIANT_ID]);
  };

  const gwpProperties = (source) => ({
    _gwp: GWP_PROPERTY,
    _gwp_source: source || GWP_SOURCE_PDP,
  });

  /** Properties to attach on PDP subscription ATC (or null if already marked / disabled). */
  function getPdpGwpProperties(cart) {
    if (!isGwpEnabled()) return null;
    if (cart && cartHasGwpMarker(cart)) return null;
    return gwpProperties(GWP_SOURCE_PDP);
  }

  function refreshCartUI() {
    return getCart()
      .then((cart) => {
        if (typeof window.updateCartUI === 'function') {
          window.updateCartUI(cart);
        } else {
          window.dispatchEvent(new Event('cart:refresh'));
        }
        const cartCount = document.querySelector('#cart_item_count_header');
        if (cartCount && typeof cart.item_count !== 'undefined') {
          cartCount.textContent = cart.item_count;
        }
        const varietyCount = document.querySelector('.variety-cart-count');
        if (varietyCount && typeof cart.item_count !== 'undefined') {
          varietyCount.textContent = cart.item_count;
        }
        return cart;
      })
      .catch(() => null);
  }

  async function updateLineProperties(item, nextProperties) {
    return fetchJSON('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({
        id: item.key,
        quantity: item.quantity,
        properties: nextProperties,
      }),
    });
  }

  function stripGwpFromProperties(properties) {
    const next = Object.assign({}, properties || {});
    next._gwp = '';
    next._gwp_source = '';
    return next;
  }

  async function resolveGiftIds() {
    let productId = window.RevitinGWP && window.RevitinGWP.productId;
    let variantId = window.RevitinGWP && window.RevitinGWP.variantId;

    if (productId && variantId) {
      return { productId: String(productId), variantId: String(variantId) };
    }

    try {
      const product = await fetchJSON(`/products/${GWP_HANDLE}.js`);
      productId = product?.id || null;
      variantId = product?.variants?.[0]?.id || null;
      if (productId) window.RevitinGWP.productId = productId;
      if (variantId) window.RevitinGWP.variantId = variantId;
      if (productId && variantId) {
        return { productId: String(productId), variantId: String(variantId) };
      }
    } catch (_) {}

    return null;
  }

  /**
   * Set or clear cart attributes used by Flow:
   * attributes[free_gift_product_id], attributes[free_gift_variant_id]
   */
  async function syncGiftCartAttributes(cart, { forceSet = false, forceClear = false } = {}) {
    const current = cart || (await getCart());
    const attrs = (current && current.attributes) || {};
    const shouldHave = forceSet || (!forceClear && cartHasGwpMarker(current));
    const hasAttrs = cartHasGiftAttributes(current);

    if (shouldHave) {
      const ids = await resolveGiftIds();
      if (!ids) return current;

      const productMatches = String(attrs[ATTR_PRODUCT_ID] || '') === ids.productId;
      const variantMatches = String(attrs[ATTR_VARIANT_ID] || '') === ids.variantId;
      if (productMatches && variantMatches) return current;

      return fetchJSON('/cart/update.js', {
        method: 'POST',
        body: JSON.stringify({
          attributes: {
            [ATTR_PRODUCT_ID]: ids.productId,
            [ATTR_VARIANT_ID]: ids.variantId,
          },
        }),
      });
    }

    if (hasAttrs || forceClear) {
      return fetchJSON('/cart/update.js', {
        method: 'POST',
        body: JSON.stringify({
          attributes: {
            [ATTR_PRODUCT_ID]: '',
            [ATTR_VARIANT_ID]: '',
          },
        }),
      });
    }

    return current;
  }

  /**
   * Stamp GWP on the first subscription line if the cart has any subscription
   * and no marker yet. Includes cart subscription upsells.
   */
  async function markGiftForSubscription(source = GWP_SOURCE_CART) {
    try {
      if (!isGwpEnabled()) return null;

      const cart = await getCart();
      if (cartHasGwpMarker(cart)) {
        await syncGiftCartAttributes(cart, { forceSet: true });
        return await refreshCartUI();
      }

      const subs = (cart.items || []).filter(isSubscriptionItem);
      if (!subs.length) return cart;

      const target = subs[0];
      const nextProps = Object.assign({}, target.properties || {}, gwpProperties(source));

      await updateLineProperties(target, nextProps);
      await syncGiftCartAttributes(null, { forceSet: true });
      return await refreshCartUI();
    } catch (err) {
      console.warn('Revitin GWP: mark skipped', err);
      return null;
    }
  }

  /** PDP ATC helper — same as cart auto-mark, source tagged as pdp. */
  async function markGiftFromPdp() {
    return markGiftForSubscription(GWP_SOURCE_PDP);
  }

  /** Called after ATC when line properties were already included in the add payload. */
  async function ensureGiftAttributes() {
    try {
      if (!isGwpEnabled()) return null;
      await syncGiftCartAttributes(null, { forceSet: true });
      return await refreshCartUI();
    } catch (err) {
      console.warn('Revitin GWP: attribute sync skipped', err);
      return null;
    }
  }

  let isEnforcing = false;
  let lastCartState = null;

  /**
   * Cart enforcement:
   * - If any subscription exists → ensure one GWP marker + gift attributes
   * - If none remain → clear markers + attributes
   * - Cleanup stray gift product lines
   * Never marks on wholesale / blocked templates (isGwpEnabled false).
   */
  async function enforceCartRules() {
    if (isEnforcing) return;
    isEnforcing = true;

    try {
      const cart = await getCart();
      const cartStateKey = JSON.stringify({
        items: (cart.items || []).map((i) => ({
          id: i.id,
          q: i.quantity,
          sp: !!i.selling_plan_allocation,
          gwp: hasGwpMarker(i),
          giftProduct: isGiftProduct(i),
        })),
        attrs: {
          p: cart.attributes && cart.attributes[ATTR_PRODUCT_ID],
          v: cart.attributes && cart.attributes[ATTR_VARIANT_ID],
        },
      });

      if (lastCartState === cartStateKey) return;
      lastCartState = cartStateKey;

      const giftProducts = (cart.items || []).filter(isGiftProduct);
      const markedItems = (cart.items || []).filter(hasGwpMarker);
      const subscriptionItems = (cart.items || []).filter(isSubscriptionItem);
      const hasSubscription = subscriptionItems.length > 0;
      let didChange = false;

      // Wholesale or global GWP off: strip markers + attributes so Flow never adds the gift
      if ((window.RevitinGWP && window.RevitinGWP.isWholesale) || !isGwpEnabled()) {
        if (markedItems.length) {
          for (const item of markedItems) {
            await updateLineProperties(item, stripGwpFromProperties(item.properties));
            didChange = true;
          }
        }
        if (cartHasGiftAttributes(cart)) {
          await syncGiftCartAttributes(cart, { forceClear: true });
          didChange = true;
        }
        if (didChange) {
          lastCartState = null;
          await refreshCartUI();
        }
        return;
      }

      // Cleanup: never keep the real gift product in cart
      if (giftProducts.length) {
        const updates = {};
        giftProducts.forEach((item) => {
          updates[item.key] = 0;
        });
        await fetchJSON('/cart/update.js', {
          method: 'POST',
          body: JSON.stringify({ updates }),
        });
        didChange = true;
      }

      // Clear marker when no subscriptions remain, or when marker sits on a non-subscription line
      const toClear = markedItems.filter((item) => !hasSubscription || !item.selling_plan_allocation);
      for (const item of toClear) {
        await updateLineProperties(item, stripGwpFromProperties(item.properties));
        didChange = true;
      }

      // Keep only one marker if somehow duplicated
      let stillMarked = markedItems.filter((item) => hasSubscription && item.selling_plan_allocation);
      if (stillMarked.length > 1) {
        for (let i = 1; i < stillMarked.length; i++) {
          await updateLineProperties(stillMarked[i], stripGwpFromProperties(stillMarked[i].properties));
          didChange = true;
        }
        stillMarked = [stillMarked[0]];
      }

      // Auto-mark: any subscription (including cart upsell) qualifies — once
      if (hasSubscription && stillMarked.length === 0 && isGwpEnabled()) {
        const freshCart = didChange ? await getCart() : cart;
        const target = (freshCart.items || []).find(isSubscriptionItem);
        if (target) {
          await updateLineProperties(
            target,
            Object.assign({}, target.properties || {}, gwpProperties(GWP_SOURCE_CART))
          );
          didChange = true;
        }
      }

      // Sync cart attributes with marker state
      const cartAfter = didChange ? await getCart() : cart;
      if (cartHasGwpMarker(cartAfter)) {
        const before = cartHasGiftAttributes(cartAfter);
        await syncGiftCartAttributes(cartAfter, { forceSet: true });
        if (!before) didChange = true;
      } else if (cartHasGiftAttributes(cartAfter)) {
        await syncGiftCartAttributes(cartAfter, { forceClear: true });
        didChange = true;
      }

      if (didChange) {
        lastCartState = null;
        await refreshCartUI();
      }
    } catch (err) {
      console.warn('Revitin GWP: cart enforce error', err);
    } finally {
      isEnforcing = false;
    }
  }

  function scheduleEnforce(delay = 100) {
    clearTimeout(scheduleEnforce._t);
    scheduleEnforce._t = setTimeout(() => enforceCartRules(), delay);
  }

  function initEnforcement() {
    if (window.__revitinGwpEnforceInit) return;
    window.__revitinGwpEnforceInit = true;

    const originalFetch = window.fetch;
    window.fetch = function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      const isCartChange =
        url.includes('/cart/add') ||
        url.includes('/cart/update') ||
        url.includes('/cart/change');

      if (isCartChange) {
        return originalFetch.apply(this, args).then((response) => {
          if (response.ok) scheduleEnforce(120);
          return response;
        });
      }
      return originalFetch.apply(this, args);
    };

    const wrapUpdateCartUI = () => {
      if (typeof window.updateCartUI !== 'function' || window.updateCartUI.__gwpWrapped) return;
      const original = window.updateCartUI;
      window.updateCartUI = function (cart) {
        const result = original.call(this, cart);
        scheduleEnforce(120);
        return result;
      };
      window.updateCartUI.__gwpWrapped = true;
    };

    wrapUpdateCartUI();
    document.addEventListener('DOMContentLoaded', wrapUpdateCartUI);
    setTimeout(wrapUpdateCartUI, 500);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => scheduleEnforce(400));
    } else {
      scheduleEnforce(400);
    }
  }

  window.RevitinGWP.getPdpGwpProperties = getPdpGwpProperties;
  window.RevitinGWP.markGiftFromPdp = markGiftFromPdp;
  window.RevitinGWP.markGiftForSubscription = markGiftForSubscription;
  window.RevitinGWP.ensureGiftAttributes = ensureGiftAttributes;
  window.RevitinGWP.syncGiftCartAttributes = syncGiftCartAttributes;
  // Back-compat alias used by ATC hooks
  window.RevitinGWP.addGiftFromPdp = markGiftFromPdp;
  window.RevitinGWP.enforceCartRules = enforceCartRules;
  window.RevitinGWP.hasGwpMarker = hasGwpMarker;
  window.RevitinGWP.PROPERTY = GWP_PROPERTY;
  window.RevitinGWP.SOURCE = GWP_SOURCE_PDP;

  initEnforcement();
})();
